import * as _ from 'lodash-es';
import entityPath from './entityPath';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

const POST_PATH_POST_FRAGMENT = gql(`
  fragment postPath_post on RootPostFields {
    id
    title
  }
`);

export const OPEN_INSIGHTS_URL_PARAM = 'insights-panel';

type GetPostPathOptions = {
  shareSecret?: string;
  histories?: '' | 'last' | `${number}-${number}`;
  openPostInsights?: boolean;
};

const getPostPath = (
  post: DocumentType<typeof POST_PATH_POST_FRAGMENT>,
  prefix: 'embed' | 'posts',
  options: GetPostPathOptions,
) => {
  const searchParams = new URLSearchParams();

  if (options.shareSecret) {
    searchParams.append('shr', options.shareSecret);
  }

  if (options.histories != null) {
    searchParams.append('histories', options.histories);
  }

  if (options.openPostInsights) {
    searchParams.append(OPEN_INSIGHTS_URL_PARAM, 'true');
  }

  const queryString = searchParams.toString();
  return `/${prefix}/${entityPath(post.title, post.id)}${queryString ? `?${queryString}` : ''}`;
};

const POST_EMBED_PATH_POST_FRAGMENT = gql(`
  fragment postEmbedPath_post on RootPostFields {
    ...postPath_post
  }
`);

export function postEmbedPath(
  post: DocumentType<typeof POST_EMBED_PATH_POST_FRAGMENT>,
  shareSecret?: string,
) {
  return getPostPath(post, 'embed', { shareSecret });
}

export function createPostPath(): string;
export function createPostPath(params: { type: 'template' }): string;
export function createPostPath(params: { topicId?: string }): string;
export function createPostPath(params: { templateId: string }): string;
export function createPostPath(params: { library: string }): string;
export function createPostPath(
  params?:
    | { type: 'template' }
    | { topicId?: string }
    | { templateId: string }
    | { library: string },
): string {
  const allowedParams: Record<string, string> = _.pickBy(
    _.pick(params ?? {}, ['topicId', 'templateId', 'type', 'library']),
  );
  const queryString = new URLSearchParams(allowedParams).toString();
  const search = queryString && `?${queryString}`;

  return `/drafts/new${search}`;
}

type PostPathOptions = GetPostPathOptions & {
  anchor?: string;
};

const postPath = (
  post: DocumentType<typeof POST_EMBED_PATH_POST_FRAGMENT>,
  options?: PostPathOptions,
) => {
  const link = getPostPath(post, 'posts', options ?? {});
  const hasHash = options?.anchor?.startsWith('#');
  return options?.anchor
    ? `${link}${hasHash ? '' : '#'}${options.anchor}`
    : link;
};

const PUBLIC_POST_PATH_POST_FRAGMENT = gql(`
  fragment publicPostPath_post on RootPostFields {
    ...postPath_post
  }
`);

export const publicPostPath = (
  post: DocumentType<typeof PUBLIC_POST_PATH_POST_FRAGMENT>,
  options?: PostPathOptions,
) => `/public${postPath(post, options)}`;

export default postPath;
